import API_BASE_URL from './apiBaseUrl';

class WebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectTimer = null;
    this.reconnectDelay = 5000;
    this.isConnecting = false;
    this.shouldReconnect = true;
    this.authenticated = false;
    this.subscriptions = new Set();
  }

  getWebSocketUrl() {
    const apiUrl = API_BASE_URL;
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = apiUrl.replace(/^https?:\/\//, '');
    return `${wsProtocol}://${wsHost}/ws`;
  }

  connect(token) {
    if (this.isConnecting || this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    this.shouldReconnect = true;
    this.isConnecting = true;
    this.authenticated = false;

    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
      this.ws.close();
    }

    const socket = new WebSocket(this.getWebSocketUrl());
    this.ws = socket;

    socket.onopen = () => {
      if (this.ws !== socket) return;
      this.isConnecting = false;
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      if (token) {
        this.send({ type: 'auth', payload: { token } });
      } else {
        this.shouldReconnect = false;
        this.emit('auth_error', { type: 'auth_error', message: 'Authentication token is required' });
      }
    };

    socket.onmessage = (event) => {
      if (this.ws !== socket) return;
      try {
        const message = JSON.parse(event.data);
        const eventType = message.type || 'message';
        if (eventType === 'auth_ack') {
          const wasAuthenticated = this.authenticated;
          this.authenticated = true;
          this.emit('auth_ack', message);
          if (!wasAuthenticated) {
            this.resubscribeToDevices();
            this.emit('connected', message);
          }
          return;
        }
        if (eventType === 'auth_error') {
          this.authenticated = false;
          this.shouldReconnect = false;
          this.emit('auth_error', message);
          if (socket.readyState === WebSocket.OPEN) socket.close();
          return;
        }
        if (!this.authenticated) {
          this.emit('protocol_error', { type: 'protocol_error', message: 'Message received before authentication' });
          return;
        }
        this.emit(eventType, message);
      } catch (error) {
        console.error('[WebSocket] Failed to parse message:', error);
      }
    };

    socket.onerror = (error) => {
      if (this.ws !== socket) return;
      this.isConnecting = false;
      this.emit('error', error);
    };

    socket.onclose = () => {
      if (this.ws !== socket) return;
      this.isConnecting = false;
      this.authenticated = false;
      this.emit('disconnected');
      this.ws = null;
      if (this.shouldReconnect) {
        this.scheduleReconnect(token);
      }
    };
  }

  scheduleReconnect(token) {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(token);
    }, this.reconnectDelay);
  }

  disconnect() {
    this.shouldReconnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const socket = this.ws;
    this.ws = null;
    if (socket) socket.close();
    this.isConnecting = false;
    this.authenticated = false;
  }

  send(payload) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false;
    }
    this.ws.send(JSON.stringify(payload));
    return true;
  }

  subscribeToDevice(deviceId) {
    if (!deviceId) return false;
    this.subscriptions.add(deviceId);
    if (!this.authenticated) return true;
    return this.send({ type: 'subscribe', payload: { deviceId } });
  }

  unsubscribeFromDevice(deviceId) {
    if (!deviceId) return false;
    this.subscriptions.delete(deviceId);
    if (!this.authenticated) return true;
    return this.send({ type: 'unsubscribe', payload: { deviceId } });
  }

  syncDeviceSubscriptions(deviceIds = []) {
    const nextSubscriptions = new Set(deviceIds.filter(Boolean));
    const removed = [...this.subscriptions].filter((deviceId) => !nextSubscriptions.has(deviceId));
    const added = [...nextSubscriptions].filter((deviceId) => !this.subscriptions.has(deviceId));
    this.subscriptions = nextSubscriptions;

    if (this.authenticated) {
      removed.forEach((deviceId) => this.send({ type: 'unsubscribe', payload: { deviceId } }));
      added.forEach((deviceId) => this.send({ type: 'subscribe', payload: { deviceId } }));
    }
    return this.subscriptions.size;
  }

  resubscribeToDevices() {
    if (!this.authenticated) return 0;
    let sent = 0;
    this.subscriptions.forEach((deviceId) => {
      if (this.send({ type: 'subscribe', payload: { deviceId } })) sent += 1;
    });
    return sent;
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;
    const updated = this.listeners.get(event).filter((listener) => listener !== callback);
    this.listeners.set(event, updated);
  }

  emit(event, data) {
    const listeners = this.listeners.get(event) || [];
    listeners.forEach((listener) => {
      try {
        listener(data);
      } catch (error) {
        console.error(`[WebSocket] ${event} listener error:`, error);
      }
    });
  }

  getConnectionState() {
    if (!this.ws) return 'disconnected';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return this.authenticated ? 'connected' : 'authenticating';
      case WebSocket.CLOSING:
        return 'closing';
      default:
        return 'disconnected';
    }
  }
}

const websocketService = new WebSocketService();

export default websocketService;
